import { Button, Modal, Skeleton } from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import EmailEditor from "../../../shared/emailEditor/EmailEditor";
import {
  fetchEmailSignature,
  updateEmailSignature,
} from "../../../store/renewalsSearch/renewalsSearchThunks";

// Lets the sender change their own email signature. Loads the saved signature each time it
// opens and reports the saved HTML back through onSaved.
const EditSignatureModal = ({ open, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setLoading(true);
    dispatch(fetchEmailSignature())
      .unwrap()
      .then((settings) => {
        if (!cancelled) setDraft(settings.email_signature ?? "");
      })
      .catch(() => {
        // Shown by the API error notifications; the editor opens empty
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, dispatch]);

  const handleSave = async () => {
    const editor = editorRef.current;
    // An empty editor is stored as a clean empty string instead of "<p><br></p>"
    const html = !editor || editor.isEmpty() ? "" : editor.getHTML();

    setSaving(true);
    try {
      await dispatch(updateEmailSignature({ email_signature: html })).unwrap();
      onSaved(html);
    } catch (_) {
      // Shown by the API error notifications
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Edit Email Signature"
      width={760}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      <p style={{ marginBottom: 8 }}>
        This signature is placed wherever the renewal email template puts it.
      </p>

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <EmailEditor
          ref={editorRef}
          defaultValue={draft}
          placeholder="Paste or type your email signature here, then adjust font, size and colour…"
          minHeight={160}
        />
      )}
    </Modal>
  );
};

export default EditSignatureModal;
